import Tree, {
  ItemId,
  moveItemOnTree,
  mutateTree,
  RenderItemParams,
  TreeData,
  TreeDestinationPosition,
  TreeItem,
  TreeSourcePosition,
} from "@atlaskit/tree";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import {
  Autocomplete,
  Box,
  Button,
  ClickAwayListener,
  Divider,
  IconButton,
  IconButtonProps,
  OutlinedInput,
  Stack,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Modal, { CommonModalProps } from "..";
import { userService } from "../../../Core/Api/services";
import { DbUserBasicInfos } from "../../../Core/Api/Types/db-user-basic-infos";
import { GroupPlanningUserGroupInfos } from "../../../Core/Api/Types/group-planning-user-group-infos";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { ConsoleLogger } from "@microsoft/signalr/dist/esm/Utils";
import { useTranslation } from "react-i18next";

export type ModalCreateOrUpdatePlanningUserGroupElementProps = {
  mode: "create" | "update";
  existingGroup?: GroupPlanningUserGroupInfos;
  addUserGroupToGroup: (userGroup: GroupPlanningUserGroupInfos) => void;
  editUserGroupInGroup: (userGroup: GroupPlanningUserGroupInfos) => void;
};

export type ModalCreateOrUpdatePlanningUserGroupProps =
  CommonModalProps<ModalCreateOrUpdatePlanningUserGroupElementProps>;

function buildTreeFromObject(items: Record<ItemId, TreeItem>, nodeId: ItemId): GroupPlanningUserGroupInfos | undefined {
  if (items[nodeId].data.type === "user") return undefined;

  let usersIds: number[] = [];
  items[nodeId].children.forEach((child) => {
    if (items[child].data.type === "user") usersIds.push(items[child].data.id);
  });

  const node: GroupPlanningUserGroupInfos = {
    name: items[nodeId].data.name,
    usersIds: usersIds,
    childrens: [],
  };

  if (items[nodeId]?.children) {
    items[nodeId].children.forEach((childId: ItemId) => {
      const childNode = buildTreeFromObject(items, childId);
      if (childNode) node.childrens.push(childNode);
    });
  }

  return node;
}

function buildObjectFromTree(root: GroupPlanningUserGroupInfos): Record<ItemId, TreeItem> {
  let idCounter = 0;
  const idMap: Map<GroupPlanningUserGroupInfos, number> = new Map();

  function traverse(node: GroupPlanningUserGroupInfos) {
    const nodeId = idCounter++;
    idMap.set(node, nodeId);

    const childrenIds: number[] = [];

    node.childrens.forEach((child) => {
      childrenIds.push(traverse(child));
    });

    return nodeId;
  }

  traverse(root);

  const obj: Record<ItemId, TreeItem> = {};

  idMap.forEach((nodeId, node) => {
    const childrenIds = node.childrens.map((child) => idMap.get(child)!);
    obj[nodeId] = {
      id: nodeId,
      hasChildren: childrenIds.length > 0,
      isChildrenLoading: false,
      isExpanded: false,
      children: childrenIds,
      data: {
        name: node.name,
        type: "group",
        selectedUsersIds: node.usersIds,
      },
    };
  });

  return obj;
}

function getChildrenIds(items: Record<ItemId, TreeItem>, parentId: ItemId): ItemId[] {
  const parent = items[parentId];

  if (!parent) return [];

  let childrenIds: ItemId[] = [];

  parent.children.forEach((childId) => {
    const grandChildrenIds = getChildrenIds(items, childId);
    childrenIds = [...childrenIds, childId, ...grandChildrenIds];
  });

  return childrenIds;
}

const ModalCreateOrUpdatePlanningUserGroup = React.forwardRef<any, ModalCreateOrUpdatePlanningUserGroupProps>(
  (props, ref) => {
    const { t } = useTranslation();
    const { mode, existingGroup, addUserGroupToGroup, editUserGroupInGroup, closeModal } = props;

    const [users, setUsers] = useState<DbUserBasicInfos[]>([]);

    const [rootName, setRootName] = useState("");
    const isRootNameValid = useAppSelector(GetFormIsValid("create-update-user-group"));

    const [folderName, setFolderName] = useState("");
    const [selectedUsers, setSelectedUsers] = useState<DbUserBasicInfos[]>([]);

    const [treeData, setTreeData] = useState<TreeData>({
      rootId: 0,
      items: {
        0: {
          children: [],
          id: 0,
          hasChildren: true,
          isExpanded: false,
          isChildrenLoading: false,
          data: {
            name: "__root__",
            selectedUsersIds: [],
          },
        },
      },
    });

    const renderItem = (renderItemParams: RenderItemParams) => {
      const userIds: number[] = renderItemParams.item.children
        .filter((c) => treeData.items[c].data.type === "user")
        .map((c) => treeData.items[c].data.id);
      return (
        <div
          ref={renderItemParams.provided.innerRef}
          {...renderItemParams.provided.draggableProps}
          {...renderItemParams.provided.dragHandleProps}
        >
          <TreeCard
            item={renderItemParams.item}
            onExpand={onExpand}
            onCollapse={onCollapse}
            onClickAddGroup={createGroupAtLevel}
            selectableUsersList={users}
            selectedUsersIds={userIds}
            onClickAddUsers={addUsersAtLevel}
            onClickDeleteItem={deleteItem}
          />
        </div>
      );
    };

    const onExpand = (itemId: ItemId) => {
      setTreeData(mutateTree(treeData, itemId, { isExpanded: true }));
    };

    const onCollapse = (itemId: ItemId) => {
      setTreeData(mutateTree(treeData, itemId, { isExpanded: false }));
    };

    const onDragEnd = (source: TreeSourcePosition, destination?: TreeDestinationPosition) => {
      if (!destination) {
        return;
      }
      if (treeData.items[destination.parentId].data.type === "user") return;

      setTreeData(moveItemOnTree(treeData, source, destination));
    };

    const getNewId = () => {
      return Math.max(...Object.keys(treeData.items).map((k) => +k)) + 1;
    };

    const createGroupAtLevel = (groupName: string, parentId: number) => {
      const newId = getNewId();
      const newTreeData = {
        ...treeData,
      };
      newTreeData.items[newId] = {
        children: [],
        id: newId,
        hasChildren: false,
        isExpanded: false,
        isChildrenLoading: false,
        data: {
          name: groupName,
          type: "group",
          selectedUsersIds: [],
          id: undefined,
        },
      };
      const newParentChildren = newTreeData.items[parentId].children;
      newParentChildren.push(newId);
      setTreeData(
        mutateTree(newTreeData, parentId, { isExpanded: true, hasChildren: true, children: newParentChildren })
      );
    };

    const addUsersAtLevel = (selectedUsers: DbUserBasicInfos[], parentId: number) => {
      const newTreeData = {
        ...treeData,
      };
      let newChildrenIds: ItemId[] = [];
      selectedUsers.forEach((user) => {
        const newId = getNewId();
        newChildrenIds.push(newId);

        newTreeData.items[newId] = {
          children: [],
          id: newId,
          hasChildren: false,
          isExpanded: false,
          isChildrenLoading: false,
          data: {
            name: user.name,
            type: "user",
            selectedUsersIds: [],
            id: user.id,
          },
        };
      });

      const newParentChildren = newTreeData.items[parentId].children.filter(
        (c) => newTreeData.items[c].data.type === "group"
      );
      newParentChildren.push(...newChildrenIds);
      const newParentData = {
        ...newTreeData.items[parentId].data,
        selectedUsersIds: selectedUsers.map((user) => user.id),
      };
      setTreeData(
        mutateTree(newTreeData, parentId, {
          isExpanded: newParentChildren.length > 0,
          hasChildren: newParentChildren.length > 0,
          children: newParentChildren,
          data: newParentData,
        })
      );
    };

    const deleteItem = (itemId: ItemId) => {
      const newTreeItems = {};

      const keysToIgnore = getChildrenIds(treeData.items, itemId).map((id) => id.toString());
      keysToIgnore.push(itemId.toString());

      Object.keys(treeData.items).forEach((key) => {
        if (!keysToIgnore.includes(key)) {
          newTreeItems[key] = treeData.items[key];
        }
      });
      let newTreeData: TreeData = {
        rootId: 0,
        items: newTreeItems,
      };

      Object.keys(newTreeData.items).forEach((key) => {
        if (newTreeData.items[key].children.includes(itemId)) {
          newTreeData.items[key].children = newTreeData.items[key].children.filter((c) => c !== itemId);
          newTreeData = mutateTree(newTreeData, key, {
            children: newTreeData.items[key].children,
            hasChildren: newTreeData.items[key].children.length > 0,
          });
        }
      });

      setTreeData({ ...newTreeData });
    };

    const handleAddGroupAtTopLevel = () => {
      createGroupAtLevel(folderName, 0);
      setFolderName("");
    };

    const handleAddUsersAtTopLevel = () => {
      addUsersAtLevel(selectedUsers, 0);
      setSelectedUsers([]);
    };

    const saveUserGroup = () => {
      const tree = buildTreeFromObject(treeData.items, 0);
      if (tree) {
        tree.name = rootName;
        if (mode === "create") addUserGroupToGroup(tree);
        else editUserGroupInGroup(tree);

        closeModal();
      }
    };

    const loadTreeDataFromUserGroup = (
      userGroup: GroupPlanningUserGroupInfos,
      usersList: DbUserBasicInfos[]
    ): TreeData => {
      const groups = buildObjectFromTree(userGroup);
      const items = { ...groups };
      let maxId = Math.max(...Object.keys(items).map((k) => +k));

      Object.keys(groups).forEach((key) => {
        const usersToAdd: DbUserBasicInfos[] = [];
        groups[key].data.selectedUsersIds.forEach((userId) => {
          const user = usersList.find((u) => u.id === userId);
          if (user) usersToAdd.push(user);
        });

        usersToAdd.forEach((user) => {
          const newId = ++maxId;
          items[newId] = {
            id: newId,
            children: [],
            hasChildren: false,
            isChildrenLoading: false,
            isExpanded: false,
            data: {
              name: user.name,
              type: "user",
              selectedUsersIds: [],
              id: user.id,
            },
          };
          items[key].children.push(newId);
        });
        items[key].hasChildren = items[key].children.length > 0;
      });

      return {
        rootId: 0,
        items,
      };
    };

    useEffect(() => {
      userService.GetAllUsersBasic().then((response) => {
        setUsers(response.users || []);
        if (existingGroup) {
          const data = loadTreeDataFromUserGroup(existingGroup, response.users || []);
          setRootName(existingGroup.name);
          setTreeData(data);
        }
      });
    }, []);

    return (
      <Modal
        title={t("modal:title.createUserGroup")}
        reference={ref}
        closeModal={closeModal}
        width={600}
        footer={
          <Stack spacing={1}>
            {/* <Alert severity="warning">
            Changes will only be stored in database when you press "Save changes" button on previous panel group
          </Alert> */}
            <Stack direction="row" justifyContent="space-between">
              <Button variant="outlined" onClick={closeModal}>
                {t("common:cancel")}
              </Button>
              <Button
                variant="outlined"
                color="success"
                onClick={saveUserGroup}
                disabled={!isRootNameValid || Object.keys(treeData.items).length <= 1}
              >
                {t("common:save")}
              </Button>
            </Stack>
          </Stack>
        }
      >
        <Stack spacing={2}>
          <FormInputText
            label={t("field:label.nameYourUserGroup")}
            validationProps={{
              formId: "create-update-user-group",
              fieldId: "root-name",
              required: mode === "create",
              initialValue: existingGroup?.name,
            }}
            value={rootName}
            onChange={(e) => setRootName(e.target.value)}
            disabled={mode === "update"}
          />
          <Divider />
          <Stack direction="row" spacing={1}>
            <OutlinedInput
              size="small"
              sx={{ width: 300 }}
              value={folderName}
              onChange={(event) => setFolderName(event.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            <Button variant="outlined" onClick={handleAddGroupAtTopLevel} disabled={folderName.length === 0}>
              {t("button:createGroupAtTopLevel")}
            </Button>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Autocomplete
              popupIcon={<ExpandMoreRoundedIcon />}
              value={selectedUsers}
              options={users}
              getOptionLabel={(option) => option.name || "N/A"}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              multiple
              onClick={(e) => e.stopPropagation()}
              onOpen={(e) => e.stopPropagation()}
              onChange={(_, value) => setSelectedUsers(value)}
              sx={{ width: 300 }}
              renderInput={(params) => (
                <OutlinedInput
                  {...params.InputProps}
                  inputProps={params.inputProps}
                  placeholder={t("field:placeholder.addUsers")}
                  size="small"
                  sx={{ width: "100%" }}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            />
            <Button variant="outlined" onClick={handleAddUsersAtTopLevel} disabled={selectedUsers.length === 0}>
              {t("button:addUserAtTopLevel")}
            </Button>
          </Stack>
          <Divider />
          <Tree
            tree={treeData}
            renderItem={renderItem}
            onExpand={onExpand}
            onCollapse={onCollapse}
            onDragEnd={onDragEnd}
            isDragEnabled
            isNestingEnabled
          />
        </Stack>
      </Modal>
    );
  }
);

export default ModalCreateOrUpdatePlanningUserGroup;

interface TreeCardProps {
  item: TreeItem;
  onExpand: (itemId: ItemId) => void;
  onCollapse: (itemId: ItemId) => void;
  onClickAddGroup: (groupName: string, parentId: number) => void;
  selectableUsersList: DbUserBasicInfos[];
  selectedUsersIds: number[];
  onClickAddUsers: (selectedUsers: DbUserBasicInfos[], parentId: number) => void;
  onClickDeleteItem: (itemId: number) => void;
}

const TreeCard = (props: TreeCardProps) => {
  const { t } = useTranslation();
  const {
    item,
    onExpand,
    onCollapse,
    onClickAddGroup,
    selectableUsersList,
    selectedUsersIds,
    onClickAddUsers,
    onClickDeleteItem,
  } = props;

  const [openFolderTooltip, setOpenFolderTooltip] = useState(false);
  const [openUserTooltip, setOpenUserTooltip] = useState(false);

  const [folderName, setFolderName] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<DbUserBasicInfos[]>([]);

  const handleClickAddGroup = () => {
    onClickAddGroup(folderName, item.id as number);
    setOpenUserTooltip(false);
    setFolderName("");
  };

  const handleOnChangeUsers = (event: React.SyntheticEvent<Element, Event>, value: DbUserBasicInfos[]) => {
    event.stopPropagation();
    setSelectedUsers(value);
  };

  const handleClickAddUsers = () => {
    onClickAddUsers(selectedUsers, item.id as number);
    setOpenUserTooltip(false);
    setSelectedUsers([]);
  };

  useEffect(() => {
    let output: DbUserBasicInfos[] = [];
    selectedUsersIds.forEach((id) => {
      const user = selectableUsersList.find((u) => u.id === id);
      if (user) output.push(user);
    });
    setSelectedUsers(output);
  }, [selectedUsersIds, openUserTooltip]);

  return (
    <Wrapper
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent="space-between"
      //   onClick={() => (item.isExpanded ? onCollapse(item.id) : onExpand(item.id))}
    >
      <LeftStack direction="row" spacing={1} alignItems="center" flex={1}>
        {item.hasChildren ? (
          <ExpandMore
            expand={item.isExpanded || false}
            onClick={() => (item.isExpanded ? onCollapse(item.id) : onExpand(item.id))}
            aria-expanded={item.isExpanded}
            sx={{ ml: 0 }}
          >
            <ExpandMoreIcon />
          </ExpandMore>
        ) : (
          <Box sx={{ mr: 4 }}></Box>
        )}
        {item.data && item.data.type === "group" && <FolderOutlinedIcon />}
        {item.data && item.data.type === "user" && <PersonOutlineOutlinedIcon />}

        <Typography variant="body1">{item.data?.name || "N/A"}</Typography>
      </LeftStack>
      {item.data.type === "group" && (
        <Stack direction="row">
          <ClickAwayListener onClickAway={() => setOpenFolderTooltip(false)}>
            <Tooltip
              PopperProps={{
                disablePortal: true,
              }}
              onClose={() => setOpenFolderTooltip(false)}
              open={openFolderTooltip}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={
                <Stack direction="row" spacing={1}>
                  <OutlinedInput
                    size="small"
                    value={folderName}
                    onChange={(event) => setFolderName(event.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Button size="small" onClick={handleClickAddGroup} disabled={folderName.length === 0}>
                    {t("common:add")}
                  </Button>
                </Stack>
              }
            >
              <IconButton onClick={() => setOpenFolderTooltip(true)}>
                <CreateNewFolderOutlinedIcon color="primary" />
              </IconButton>
            </Tooltip>
          </ClickAwayListener>
          <ClickAwayListener onClickAway={() => setOpenUserTooltip(false)}>
            <Tooltip
              PopperProps={{
                disablePortal: true,
              }}
              onClose={() => setOpenUserTooltip(false)}
              open={openUserTooltip}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={
                <Stack direction="row" spacing={1}>
                  <Autocomplete
                    popupIcon={<ExpandMoreRoundedIcon />}
                    value={selectedUsers}
                    options={selectableUsersList}
                    getOptionLabel={(option) => option.name || "N/A"}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    multiple
                    onClick={(e) => e.stopPropagation()}
                    onOpen={(e) => e.stopPropagation()}
                    onChange={handleOnChangeUsers}
                    sx={{ width: 300 }}
                    renderInput={(params) => (
                      <OutlinedInput
                        {...params.InputProps}
                        inputProps={params.inputProps}
                        placeholder={t("field:placeholder.addUsers")}
                        size="small"
                        sx={{ width: "100%" }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  />
                  <Button onClick={handleClickAddUsers}>{t("common:apply")}</Button>
                </Stack>
              }
            >
              <IconButton onClick={() => setOpenUserTooltip(true)}>
                <PersonAddOutlinedIcon color="primary" />
              </IconButton>
            </Tooltip>
          </ClickAwayListener>
          <IconButton onClick={() => onClickDeleteItem(item.id as number)}>
            <ClearOutlinedIcon color="error" />
          </IconButton>
        </Stack>
      )}
    </Wrapper>
  );
};

const Wrapper = styled(Stack)<{}>(({ theme }) => ({
  height: 34,
  paddingTop: 0,
  paddingBottom: 0,
  ".MuiIconButton-root": {
    marginRight: theme.spacing(-0.5),
  },
}));

const LeftStack = styled(Stack)(({ theme }) => ({
  overflow: "hidden",
  div: {
    overflow: "hidden",
  },
  ".tree-row-title": {
    ...theme.helpers(theme).ellipsis,
  },
}));

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? "rotate(-90deg)" : "rotate(0deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
}));
